import React, { useEffect } from 'react';
import CrewStickinessPanel from './CrewStickinessPanel';

const BETA_MESSAGE =
  'Private beta: expect bugs, limited memes, and occasional weirdness. ' +
  'DM @gweffitapp or text Blake with screenshots if something breaks.';

export default function SettingsPanel({
  open,
  onClose,
  onEnableNotifications,
  notificationPrefs,
  onTogglePref,
  prefsSaving,
  streakFreezeCount = 0,
  appUserId = null,
  onNavigate = null,
}) {
  useEffect(() => {
    if (!open || process.env.NODE_ENV === 'production' || typeof window === 'undefined') return;
    try {
      const vw = window.innerWidth || document.documentElement.clientWidth || 0;
      const offenders = Array.from(document.querySelectorAll('body *'))
        .map((el) => {
          const rect = el.getBoundingClientRect?.();
          if (!rect) return null;
          const overflow = Math.max(0, rect.right - vw);
          if (overflow <= 1) return null;
          return {
            tag: el.tagName,
            className: String(el.className || '').slice(0, 140),
            width: Math.round(rect.width),
            right: Math.round(rect.right),
            overflow: Math.round(overflow),
          };
        })
        .filter(Boolean)
        .sort((a, b) => b.overflow - a.overflow)
        .slice(0, 5);
      if (offenders.length) {
        console.log('[layout] settings_overflow_offenders', { vw, offenders });
      }
    } catch {}
  }, [open]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black/70 p-3 sm:p-4 overflow-y-auto overscroll-contain">
      <div className="min-h-full flex items-center justify-center">
        <div className="w-full max-w-md max-w-full box-border rounded-2xl bg-slate-900 border border-slate-700 p-4 sm:p-5 space-y-4 shadow-2xl">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h2 className="text-lg font-bold text-slate-50 flex items-center gap-2">
              <span>About this beta</span>
              <span className="text-[0.65rem] font-semibold px-2 py-0.5 rounded-full bg-yellow-500/15 border border-yellow-400/60 text-yellow-200 uppercase tracking-wide">
                Beta
              </span>
            </h2>
            <p className="mt-1 text-xs sm:text-sm text-slate-300 break-words [overflow-wrap:anywhere]">
              {BETA_MESSAGE}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-slate-100 text-sm px-2 py-1 rounded-lg hover:bg-slate-800"
          >
            ✕
          </button>
        </div>

        <div className="space-y-3 text-xs sm:text-sm">
          <div className="space-y-1.5">
            <div className="font-semibold text-slate-100 flex items-center gap-1.5">
              <span>🔔 Notifications</span>
            </div>
            <p className="text-slate-300">
              Turn on push notifications so juries, verdicts, and summons can
              chase you around like an overeager group chat.
            </p>
            <button
              onClick={onEnableNotifications}
              className="mt-1 inline-flex items-center gap-1.5 rounded-full border border-purple-400/70 bg-purple-500/10 px-3 py-1.5 text-xs font-semibold text-purple-100 hover:bg-purple-500/20 active:scale-[0.98] transition"
            >
              <span>Enable notifications</span>
            </button>
            <div className="mt-3 space-y-2">
              {[
                { key: 'jury_duty', label: 'Jury Duty', desc: 'Summons and tie-break alerts.' },
                { key: 'verdicts', label: 'Verdicts', desc: 'Trial verdict + digest updates.' },
                { key: 'daily', label: 'Daily', desc: 'Daily question shares and outcomes.' },
                { key: 'streak', label: 'Streak', desc: 'Reminders before streak expiry.' },
              ].map((item) => (
                <label key={item.key} className="flex items-center justify-between rounded-lg bg-slate-800/60 px-3 py-2 text-xs text-slate-200 gap-3">
                  <span>
                    <div>{item.label}</div>
                    <div className="text-[11px] text-slate-400 mt-0.5">{item.desc}</div>
                  </span>
                  <input
                    type="checkbox"
                    checked={notificationPrefs?.[item.key] !== false}
                    disabled={prefsSaving}
                    onChange={(e) => onTogglePref?.(item.key, e.target.checked)}
                  />
                </label>
              ))}
            </div>
          </div>

          <div className="space-y-1.5">
            <div className="font-semibold text-slate-100 flex items-center gap-1.5">
              <span>🧊 Streak Freezes</span>
            </div>
            <p className="text-slate-300">
              You currently have <span className="font-semibold text-cyan-200">{streakFreezeCount}</span> streak freeze{streakFreezeCount === 1 ? '' : 's'}.
              Buy more in the Coin Shop (max 2).
            </p>
          </div>

          <CrewStickinessPanel appUserId={appUserId} isOpen={open} />

          <div className="space-y-1.5">
            <div className="font-semibold text-slate-100 flex items-center gap-1.5">
              <span>🩺 Diagnostics</span>
            </div>
            <p className="text-slate-300 break-words [overflow-wrap:anywhere]">
              Open diagnostics for a quick runtime snapshot and debug tools.
            </p>
            <button
              onClick={() => {
                onClose?.();
                if (typeof requestAnimationFrame === 'function') {
                  requestAnimationFrame(() => {
                    requestAnimationFrame(() => onNavigate?.('/diagnostics'));
                  });
                } else {
                  setTimeout(() => onNavigate?.('/diagnostics'), 0);
                }
              }}
              className="inline-flex items-center gap-1.5 rounded-full border border-slate-500 bg-slate-800/60 px-3 py-1.5 text-xs font-semibold text-slate-100 hover:bg-slate-700"
            >
              Open diagnostics
            </button>
          </div>

          <div className="space-y-1.5">
            <div className="font-semibold text-slate-100 flex items-center gap-1.5">
              <span>💬 Send feedback</span>
            </div>
            <p className="text-slate-300 break-words [overflow-wrap:anywhere]">
              Found a bug? Something confusing? Or a moment that actually
              slapped? Send it.
            </p>
            <div className="flex flex-wrap gap-2">
              <a
                href="mailto:info@gweffit.com?subject=GweffIT%20beta%20feedback&body=Screenshots%20are%20gold.%20What%20were%20you%20doing%20when%20it%20broke%3F"
                className="inline-flex items-center gap-1.5 rounded-full border border-slate-500 bg-slate-800/60 px-3 py-1.5 text-xs font-semibold text-slate-100 hover:bg-slate-700"
              >
                Email feedback
              </a>
            </div>
          </div>

          <div className="space-y-1.5">
            <div className="font-semibold text-slate-100 flex items-center gap-1.5">
              <span>🚪 Leaving the beta</span>
            </div>
            <p className="text-slate-300">
              To “leave the beta”, just delete the app from your home screen,
              close the tab, and tell your friends you were doing something
              productive instead. We&apos;ll pretend we never saw you.
            </p>
          </div>
        </div>
        </div>
      </div>
    </div>
  );
}
